import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { t } from 'i18next';
import { unionBy } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import { PhaseBadge, PhaseDrawer } from './PhaseDrawer/PhaseDrawer';
import type { PhaseOptions } from './PhaseDrawer/types';

interface PhaseSelectionProps {
  canEdit: boolean;
  canAdd?: boolean;
  hasLabels?: boolean;
  inputStyle?: React.CSSProperties;
  alignListboxRight?: boolean;
  isFormSubmitted?: boolean;
  onChange?: (option: PhaseOptions | null) => void;
  onAddPhasesFromOtherProject?: () => void;
  onPhaseCodeChange?: (option: string | null) => void;
  onPhaseNameChange?: (option: string | null) => void;
  onPhaseOptionsChange?: (options: PhaseOptions[]) => void;
  phaseOptions?: PhaseOptions[];
  selectedPhase?: PhaseOptions | null;
  disableAutoDefaultedPhase?: boolean;
  isDisabled?: boolean;
}
export const PhaseSelection = ({
  canEdit,
  canAdd = true,
  hasLabels = true,
  inputStyle,
  alignListboxRight = false,
  isFormSubmitted,
  onAddPhasesFromOtherProject,
  onChange = () => {},
  onPhaseCodeChange,
  onPhaseNameChange,
  onPhaseOptionsChange = () => {},
  phaseOptions = [],
  selectedPhase,
  disableAutoDefaultedPhase = false,
  isDisabled = false,
}: PhaseSelectionProps) => {
  const [localPhaseOptions, setLocalPhaseOptions] = useState<PhaseOptions[]>(
    phaseOptions || [],
  );
  const [userSelectedPhase, setUserSelectedPhase] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const prevPhaseNameOptionsRef = useRef<string>('');
  const prevPhaseCodeOptionsRef = useRef<string>('');

  const renderAddPhasesFromOtherProjectFooter = useCallback(
    (close: () => void) => (
      <SmallButton
        color="brandV2Black"
        data-test-id="add-phases-from-other-project-footer"
        onClick={() => {
          close();
          onAddPhasesFromOtherProject?.();
        }}
        sx={{ color: 'text.primary', width: '100%' }}
        variant="text"
      >
        {t('phases.add_from_other_project')}
      </SmallButton>
    ),
    [onAddPhasesFromOtherProject],
  );
  const dropdownFooter = onAddPhasesFromOtherProject
    ? renderAddPhasesFromOtherProjectFooter
    : undefined;

  useEffect(() => {
    setLocalPhaseOptions(phaseOptions);

    const currentName = watchPhase('name');
    const currentCode = watchPhase('code');

    const hasSelection = currentName && currentCode;
    if (hasSelection) {
      const stillValid = phaseOptions.some(
        (phase) => phase.name === currentName && phase.code === currentCode,
      );

      if (!stillValid) {
        setValue('name', '');
        setValue('code', '');
      }
    }

    onPhaseOptionsChange(phaseOptions);
  }, [JSON.stringify(phaseOptions)]);

  const tomlinsonOrderModalFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.tomlinsonOrderModal,
  });

  const {
    control: phaseControl,
    watch: watchPhase,
    setValue,
    formState: { errors: phaseErrors },
    clearErrors,
    setError,
  } = useForm<PhaseOptions>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      code: '',
    },
  });
  const watchPhaseCode = watchPhase('code') as string | null;
  const watchPhaseName = watchPhase('name') as string | null;
  const phasePopperModifiers = useMemo(
    () => [
      {
        name: 'preventOverflow',
        options: {
          altAxis: true,
          tether: false,
          rootBoundary: 'viewport',
          padding: 8,
        },
      },
    ],
    [],
  );

  const filteredPhaseOptions = useMemo(() => {
    return localPhaseOptions
      .filter((item) => item.code)
      .map((item) => (item.id.startsWith('_temp_') ? { ...item, id: '' } : item))
      .filter((item) => !(item._destroy && item.id === ''));
  }, [localPhaseOptions]);

  useEffect(() => {
    if (watchPhaseName && watchPhaseCode) {
      // set the primary phase amongst the phase options
      setLocalPhaseOptions((prev) => {
        let primarySet = false;
        return prev.map((item) => {
          if (
            item.code === watchPhaseCode &&
            item.name === watchPhaseName &&
            !primarySet
          ) {
            primarySet = true; // Mark that we've set a primary phase
            return { ...item, primary: true };
          } else {
            return { ...item, primary: undefined };
          }
        });
      });
    } else if (watchPhaseName === '' || watchPhaseCode === '') {
      setLocalPhaseOptions((prev) => prev.map((item) => ({ ...item, primary: false })));
    }
  }, [watchPhaseCode, watchPhaseName]);

  useEffect(() => {
    onPhaseOptionsChange(filteredPhaseOptions);
  }, [filteredPhaseOptions]);

  // only show distinct phase names.
  const phaseNameOptions = useMemo(() => {
    return unionBy(
      localPhaseOptions.filter((item) => !item._destroy),
      (item) => item.name,
    );
  }, [localPhaseOptions]);

  // only show phases with the same name as the selected phase.
  // don't show deleted phases.
  const phaseCodeOptions = useMemo(() => {
    return localPhaseOptions
      .filter((phase) => phase.name === watchPhaseName && phase.code)
      .filter((item) => !item._destroy)
      .map((item) => item.code);
  }, [localPhaseOptions, watchPhaseName]);

  useEffect(() => {
    if (selectedPhase) {
      setValue('name', selectedPhase.name);
      setValue('code', selectedPhase.code);
    } else {
      setValue('name', '');
      setValue('code', '');
    }
  }, [JSON.stringify(selectedPhase)]);

  useEffect(() => {
    const serialized = JSON.stringify(phaseNameOptions.map((p) => p.name));
    if (serialized === prevPhaseNameOptionsRef.current) return;
    prevPhaseNameOptionsRef.current = serialized;

    if (phaseNameOptions.length === 1 && !disableAutoDefaultedPhase) {
      setValue('name', phaseNameOptions[0].name);
    }
  }, [phaseNameOptions]);

  useEffect(() => {
    const serialized = JSON.stringify(phaseCodeOptions);
    if (serialized === prevPhaseCodeOptionsRef.current) return;
    prevPhaseCodeOptionsRef.current = serialized;

    if (phaseCodeOptions.length === 1 && !disableAutoDefaultedPhase) {
      setValue('code', phaseCodeOptions[0]);
      const selectedPhase = localPhaseOptions.find(
        (item) => item.name === watchPhaseName && item.code === phaseCodeOptions[0],
      );

      if (userSelectedPhase && selectedPhase) {
        setUserSelectedPhase(false);
        onChange(selectedPhase);
      }
    }
  }, [phaseCodeOptions]);

  useEffect(() => {
    if (tomlinsonOrderModalFeatureFlagEnabled && isFormSubmitted) {
      if (watchPhaseName) {
        clearErrors('name');
      } else {
        setError('name', { type: 'maual', message: '' });
      }

      if (watchPhaseCode) {
        clearErrors('code');
      } else {
        setError('code', { type: 'maual', message: '' });
      }
    }
  }, [
    tomlinsonOrderModalFeatureFlagEnabled,
    isFormSubmitted,
    watchPhaseName,
    watchPhaseCode,
  ]);

  useEffect(() => {
    onPhaseNameChange?.(watchPhaseName);
  }, [watchPhaseName, onPhaseNameChange]);

  useEffect(() => {
    onPhaseCodeChange?.(watchPhaseCode);
  }, [watchPhaseCode, onPhaseCodeChange]);

  return (
    <>
      <Grid size={{ xs: 6 }} style={inputStyle}>
        <AutocompleteFormField
          control={phaseControl}
          name="name"
          errors={phaseErrors}
          list={phaseNameOptions}
          label={hasLabels ? `${t('project.form.project_phase_name')}` : undefined}
          popperPlacement={alignListboxRight ? 'bottom-end' : 'bottom-start'}
          listboxWidth={500}
          popperModifiers={phasePopperModifiers}
          isRequired={tomlinsonOrderModalFeatureFlagEnabled}
          clearable={true}
          disabled={isDisabled}
          getValue={(item) => (typeof item === 'string' ? item : item?.name || '')}
          getLabel={(item) => {
            if (!item) return '';
            if (typeof item === 'string') {
              if (item === watchPhaseName && watchPhaseCode) {
                return `${item} - ${watchPhaseCode}`;
              }
              return item;
            }
            const name = item?.name ?? '';
            const code = item?.code ?? '';
            return code ? `${name} - ${code}` : name;
          }}
          clearDefaultValue={''}
          onSelect={(value) => {
            setUserSelectedPhase(true);
            setValue('name', value?.name ?? '');
            setValue('code', value?.code ?? '');
            if (!value) {
              onChange(null);
            } else {
              onChange(value);
            }
          }}
          disablePortal={false}
          {...(dropdownFooter ? { dropdownFooter } : {})}
        />
      </Grid>
      <Grid size={{ xs: 6 }} style={inputStyle}>
        <AutocompleteFormField
          control={phaseControl}
          name="code"
          list={phaseCodeOptions}
          disabled={isDisabled || !watchPhaseName}
          popperPlacement={alignListboxRight ? 'bottom-end' : 'bottom-start'}
          listboxWidth={500}
          popperModifiers={phasePopperModifiers}
          clearable={true}
          clearDefaultValue={''}
          errors={phaseErrors}
          label={hasLabels ? `${t('project.form.project_phase_code')}` : undefined}
          isRequired={
            tomlinsonOrderModalFeatureFlagEnabled
              ? true
              : !watchPhaseCode && !!watchPhaseName
          }
          getValue={(item) => item}
          getLabel={(item) => item || ''}
          onSelect={(value) => {
            if (value) {
              const selectedPhase = localPhaseOptions.find(
                (item) => item.code === value && item.name === watchPhaseName,
              );
              if (selectedPhase) {
                onChange(selectedPhase);
              }
            }
          }}
          disablePortal={false}
          {...(dropdownFooter ? { dropdownFooter } : {})}
        />
      </Grid>

      {canAdd && (
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PseudoLink
              title={t('phases.view_bulk_add')}
              action={() => setDrawerOpen(true)}
              dataUi="phase-drawer-cta"
            />
            <PhaseBadge
              count={localPhaseOptions.filter((p) => !p._destroy && p.code).length}
            />
          </Box>
        </Grid>
      )}

      {canAdd && drawerOpen && (
        <PhaseDrawer
          open
          onClose={() => setDrawerOpen(false)}
          phases={localPhaseOptions}
          readOnly={!canEdit}
          onUpdatePhase={(id, updates) => {
            const phase = localPhaseOptions.find((p) => p.id === id);
            if (phase?.primary) {
              setValue('name', updates.name);
              setValue('code', updates.code);
            }
            setLocalPhaseOptions((prev) =>
              prev.map((p) => (p.id === id ? { ...p, ...updates } : p)),
            );
          }}
          onRemovePhase={(id) => {
            const phase = localPhaseOptions.find((p) => p.id === id);
            if (
              phase?.primary ||
              (phase?.name === watchPhaseName && phase?.code === watchPhaseCode)
            ) {
              setValue('name', '');
              setValue('code', '');
              onChange(null);
            }

            const isTemp = id.startsWith('_temp_');
            if (isTemp) {
              setLocalPhaseOptions((prev) => prev.filter((p) => p.id !== id));
            } else {
              setLocalPhaseOptions((prev) =>
                prev.map((p) => (p.id === id ? { ...p, _destroy: 1 } : p)),
              );
            }
          }}
          onSetPrimary={(id) => {
            const target = localPhaseOptions.find((p) => p.id === id);
            if (!target?.code) return;

            const willBePrimary = target.primary !== true;

            setLocalPhaseOptions((prev) =>
              prev.map((p) => ({
                ...p,
                primary: p.id === id ? !p.primary : false,
              })),
            );

            if (willBePrimary) {
              setValue('name', target.name);
              setValue('code', target.code);
              onChange({ ...target, primary: true });
            } else {
              setValue('name', '');
              setValue('code', '');
              onChange(null);
            }
          }}
          onAddPhases={(entries) => {
            const newPhases = entries.map((e) => ({
              id: `_temp_${crypto.randomUUID()}`,
              name: e.name,
              code: e.code,
              primary: false,
            }));
            setLocalPhaseOptions((prev) => [...prev, ...newPhases]);
          }}
        />
      )}
    </>
  );
};
